import React, { useState, useEffect } from 'react';
import { projectApi } from '../../../../../api/projectApi';
import ToolIcon from '../../../../shared/ToolIcon/ToolIcon';
import AnalysisDashboard from './components/AnalysisDashboard';
import TimesheetsDetailView from './components/TimesheetsDetailView';
import AssignmentsDetailView from './components/AssignmentsDetailView';
import JournalDetailView from './components/JournalDetailView';
import './ProjectAnalysisTab.css';

/**
 * JSDoc for ProjectAnalysisTab orchestrator component.
 * 
 * @component
 * @param {Object} props - Component props.
 * @param {Object} props.project - The current project object.
 * @param {Object} props.currentUser - The logged-in user object.
 * @param {Function} props.onTabChange - Callback to change main tabs in the project modal.
 * @returns {React.ReactElement} The rendered orchestrator tab view.
 */
export default function ProjectAnalysisTab({ project, currentUser, onTabChange }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Sub-view management: 'dashboard' | 'timesheets' | 'assignments' | 'journal'
    const [activeSubView, setActiveSubView] = useState(() => {
        const saved = sessionStorage.getItem('projectAnalysis_activeSubView');
        if (saved) {
            sessionStorage.removeItem('projectAnalysis_activeSubView');
            return saved;
        }
        return 'dashboard';
    });
    const [detailData, setDetailData] = useState([]);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);

    // Fetch initial overview data
    useEffect(() => {
        const fetchAnalysis = async () => {
            if (!project?.id) return;
            try {
                setLoading(true);
                const res = await projectApi.getProjectAnalysis(project.id);
                setData(res);
            } catch (err) {
                console.error("Error fetching project analysis", err);
                setError("Fehler beim Laden der Analysedaten");
            } finally {
                setLoading(false);
            }
        };
        fetchAnalysis();
    }, [project?.id]);

    // Fetch sub-view detailed data when switching
    useEffect(() => {
        if (activeSubView === 'dashboard' || !project?.id) {
            setDetailData([]);
            return;
        }

        const fetchDetails = async () => {
            try {
                setDetailLoading(true);
                setDetailError(null);
                let res = [];
                if (activeSubView === 'timesheets') {
                    res = await projectApi.getProjectAnalysisTimesheets(project.id);
                } else if (activeSubView === 'assignments') {
                    res = await projectApi.getProjectAnalysisAssignments(project.id);
                } else if (activeSubView === 'journal') {
                    res = await projectApi.getProjectAnalysisJournal(project.id);
                }
                setDetailData(res);
            } catch (err) {
                console.error("Error fetching sub-view details", err);
                setDetailError("Fehler beim Laden der Details");
            } finally {
                setDetailLoading(false);
            }
        };

        fetchDetails();
    }, [activeSubView, project?.id]);

    if (loading) {
        return (
            <div className="analysis-loading-container">
                <div className="spinner"></div>
                <p>Analysedaten werden geladen...</p>
            </div>
        );
    }

    if (error) {
        return <div className="analysis-error-container">{error}</div>;
    }

    if (!data) return null;

    if (activeSubView !== 'dashboard') {
        let viewTitle = '';
        let colorThemeClass = '';
        let toolName = '';

        if (activeSubView === 'timesheets') {
            viewTitle = 'Stundennachweis-Details';
            colorThemeClass = 'blue';
            toolName = 'timesheet';
        } else if (activeSubView === 'assignments') {
            viewTitle = 'Kolonnenführer-Einsätze';
            colorThemeClass = 'orange';
            toolName = 'leader';
        } else if (activeSubView === 'journal') {
            viewTitle = 'Bautagebuch-Logs';
            colorThemeClass = 'purple';
            toolName = 'journal';
        }

        return (
            <div className="project-analysis-subview">
                {/* Back Link & Header */}
                <div className="subview-header">
                    <button className="btn-back" onClick={() => setActiveSubView('dashboard')}>
                        <span className="back-arrow">←</span> Zurück zur Übersicht
                    </button>
                    <div className="subview-title-container">
                        <div className={`icon-wrapper ${colorThemeClass} small`}>
                            <ToolIcon name={toolName} mode="svg" size={16} color="#ffffff" />
                        </div>
                        <h3>{viewTitle}</h3>
                        <span className="subview-badge">{detailData.length} Einträge</span>
                    </div>
                </div>

                {/* Sub-view Content */}
                {detailError && <div className="analysis-error-container">{detailError}</div>}
                
                {!detailError && activeSubView === 'timesheets' && (
                    <TimesheetsDetailView data={detailData} isLoading={detailLoading} project={project} />
                )}
                {!detailError && activeSubView === 'assignments' && (
                    <AssignmentsDetailView data={detailData} isLoading={detailLoading} project={project} />
                )}
                {!detailError && activeSubView === 'journal' && (
                    <JournalDetailView data={detailData} isLoading={detailLoading} project={project} />
                )}
            </div>
        );
    }

    return (
        <AnalysisDashboard
            project={project}
            data={data}
            setActiveSubView={setActiveSubView}
            onTabChange={onTabChange}
        />
    );
}
